import { toast } from 'sonner';

import { t } from '@/i18n';
import { apiErrorMessage } from '@/queries/common';

export type BulkDeleteResult = {
	deleted: string[];
	failed: { id: string; message: string }[];
};

export type BulkItemLabel = { singular: string; plural: string };

const pluralise = (count: number, label: BulkItemLabel) => `${count} ${count === 1 ? label.singular : label.plural}`;

// Requests in flight at once, so a large selection does not hit the server all together
const BULK_DELETE_CONCURRENCY = 4;

/**
 * Runs the single-item endpoint for every id (delete or restore), at most four at a time, and
 * reports per item, so one failure does not stop the rest. There is no bulk endpoint on the
 * backend; deletes are soft deletes and each item is its own transaction, so partial success
 * leaves nothing half-done.
 */
export async function runBulkDelete(
	ids: string[],
	deleteOne: (id: string) => Promise<unknown>,
): Promise<BulkDeleteResult> {
	const errors: (unknown | null)[] = new Array(ids.length).fill(null);
	let next = 0;
	// Each worker takes the next id until none are left
	const worker = async () => {
		while (next < ids.length) {
			const index = next++;
			try {
				await deleteOne(ids[index]);
			} catch (error) {
				errors[index] = error ?? new Error(t('common.errors.unexpected'));
			}
		}
	};
	await Promise.all(Array.from({ length: Math.min(BULK_DELETE_CONCURRENCY, ids.length) }, worker));

	const deleted: string[] = [];
	const failed: BulkDeleteResult['failed'] = [];
	ids.forEach((id, index) => {
		const error = errors[index];
		if (error === null) deleted.push(id);
		else failed.push({ id, message: apiErrorMessage(error) });
	});
	return { deleted, failed };
}

/** Same runner for bulk restores; `deleted` then lists the restored ids. */
export const runBulkRestore = runBulkDelete;

/**
 * One toast summarising a bulk delete: all good, partly failed, or all failed. With `onUndo`, the
 * toast offers "Undo", which restores the ids that were deleted.
 */
export function reportBulkDelete(
	result: BulkDeleteResult,
	label: BulkItemLabel,
	onUndo?: (deletedIds: string[]) => void,
) {
	const { deleted, failed } = result;
	const action =
		onUndo && deleted.length > 0 ? { label: t('common.deleted.undo'), onClick: () => onUndo(deleted) } : undefined;
	if (failed.length === 0) {
		toast.success(t('common.bulk.deleted', { items: pluralise(deleted.length, label) }), { action });
		return;
	}
	const detail = failed[0].message;
	if (deleted.length === 0) {
		toast.error(t('common.bulk.noneDeleted', { items: pluralise(failed.length, label), count: failed.length }), {
			description: detail,
		});
		return;
	}
	toast.warning(t('common.bulk.partlyDeleted', { items: pluralise(deleted.length, label), failed: failed.length }), {
		description: detail,
		action,
	});
}

/** One toast summarising a bulk restore, like `reportBulkDelete`. */
export function reportBulkRestore(result: BulkDeleteResult, label: BulkItemLabel) {
	const { deleted: restored, failed } = result;
	if (failed.length === 0) {
		toast.success(t('common.bulk.restored', { items: pluralise(restored.length, label) }));
		return;
	}
	const detail = failed[0].message;
	if (restored.length === 0) {
		toast.error(t('common.bulk.noneRestored', { items: pluralise(failed.length, label), count: failed.length }), {
			description: detail,
		});
		return;
	}
	toast.warning(t('common.bulk.partlyRestored', { items: pluralise(restored.length, label), failed: failed.length }), {
		description: detail,
	});
}
